import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsMobilePortrait } from "@/hooks/use-mobile-portrait";
import { ChevronLeft, ChevronRight, MoreHorizontal, Trash2, MessageCircle, Check, Eye, EyeOff, Maximize, Minimize, Loader2, Download, ZoomIn, ZoomOut, FileText } from "lucide-react";

import { useViewerCompat } from "@/hooks/use-viewer-compat";
import { useIndicatorMutations } from "@/hooks/use-indicator-mutations";
import { useCanvasInteractionEffects } from "@/hooks/use-canvas-interaction-effects";
import { useCommentIndicatorMutations } from "@/hooks/use-comment-indicator-mutations";
import { useMainViewingAreaUtils } from "@/hooks/use-main-viewing-area-utils";
import { useMainViewingAreaEffects } from "@/hooks/use-main-viewing-area-effects";
import { useProcessingStatus } from "@/hooks/use-processing-status";
import { useDrawingOverlay } from "@/hooks/use-drawing-overlay";
import { ProcessingSkeleton } from "@/components/processing-skeleton";
import { DrawingToolbar } from "@/components/drawing-toolbar";
import { FullscreenToolbar } from "@/components/fullscreen-toolbar";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/queryClient";
import type { Document, DocumentVersion, DocumentPage, PageIndicator, IndicatorComment, DocumentUser, CommentAttachment } from "@shared/schema";
import { PageStarRating } from "./page-star-rating";
import { useToast } from "@/hooks/use-toast";
import { VersionRestrictedModal } from "@/components/version-restricted-modal";
import { CommentThreadDialog } from "@/components/comment-thread-dialog";

type CommentWithUser = IndicatorComment & { user: DocumentUser; attachments: CommentAttachment[] };

interface TextBox {
  id: string;
  pageId: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
}

interface TextSelectionOverlayProps {
  pageId: string | null;
  canvasRef: React.RefObject<HTMLImageElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  imageToCanvasCoords: (x: number, y: number) => { x: number; y: number };
  onTextHover?: (isHovering: boolean) => void;
  onTextSelected?: (text: string, position: { x: number; y: number }) => void;
  enabled: boolean;
  selectionEnabled?: boolean;
  indicatorColor: string;
}

function TextSelectionOverlay({ 
  pageId, 
  canvasRef, 
  containerRef, 
  imageToCanvasCoords,
  onTextHover,
  onTextSelected,
  enabled,
  selectionEnabled = true,
  indicatorColor
}: TextSelectionOverlayProps) {
  // Get image dimensions from the canvas image element
  const imageWidth = canvasRef.current?.naturalWidth || 1;
  const imageHeight = canvasRef.current?.naturalHeight || 1;
  const [selectedText, setSelectedText] = useState("");
  const [selectionPosition, setSelectionPosition] = useState<{ x: number; y: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Create offscreen canvas for measuring text width
  if (!measureCanvasRef.current) {
    measureCanvasRef.current = document.createElement('canvas');
  }

  // Fetch text boxes for current page
  const { data: textBoxes = [], isLoading } = useQuery<TextBox[]>({
    queryKey: ['/api/pages', pageId, 'text-boxes'],
    enabled: enabled && !!pageId,
  });

  // Debug logging
  useEffect(() => {
    console.log('📝 TextSelectionOverlay - enabled:', enabled, 'pageId:', pageId, 'textBoxes count:', textBoxes.length, 'isLoading:', isLoading);
    if (textBoxes.length > 0) {
      console.log('📝 First 3 text boxes:', textBoxes.slice(0, 3));
    }
  }, [enabled, pageId, textBoxes, isLoading]);

  // Clear selection when page changes
  useEffect(() => {
    setSelectedText("");
    setSelectionPosition(null);
    window.getSelection()?.removeAllRanges();
  }, [pageId]);

  // Handle text selection
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    
    if (text && overlayRef.current?.contains(selection?.anchorNode || null)) {
      setSelectedText(text);
      
      // Get selection position
      const range = selection?.getRangeAt(0);
      const rect = range?.getBoundingClientRect();
      
      if (rect && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        setSelectionPosition({
          x: rect.left + rect.width / 2 - containerRect.left,
          y: rect.top - containerRect.top,
        });
      }
    } else {
      setSelectedText("");
      setSelectionPosition(null);
    }
  }, [containerRef]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [handleSelectionChange]);

  // Handle mouse move for more accurate hover detection
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Check if we're actually over a text box
    const target = e.target as HTMLElement;
    const isOverText = target.hasAttribute('data-text-box-id');
    onTextHover?.(isOverText);
  }, [onTextHover]);

  const handleMouseLeave = useCallback(() => {
    onTextHover?.(false);
  }, [onTextHover]);

  // Handle touch events for mobile support
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 0) return;
    
    // Get the element at the touch point and find the nearest text box
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
    const textBox = element?.closest('[data-text-box-id]');
    const isOverText = !!textBox;
    onTextHover?.(isOverText);
  }, [onTextHover]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 0) return;
    
    // Get the element at the touch point and find the nearest text box
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
    const textBox = element?.closest('[data-text-box-id]');
    const isOverText = !!textBox;
    onTextHover?.(isOverText);
  }, [onTextHover]);

  const handleTouchEnd = useCallback(() => {
    onTextHover?.(false);
  }, [onTextHover]);

  const handleTouchCancel = useCallback(() => {
    onTextHover?.(false);
  }, [onTextHover]);

  // Sort text boxes for vertical column-based selection in slides
  // Slides typically have: header region (top 25%, wide elements) + column content below
  // Strategy: Wide/header elements sorted by Y, column content sorted by column then Y
  const sortedTextBoxes = useMemo(() => {
    if (textBoxes.length === 0) return [];
    
    const headerYThreshold = 0.25; // Top 25% is header region
    const wideBoxThreshold = 0.5;  // Boxes spanning >50% width are headers/titles
    
    // Separate wide/header boxes from column content
    const wideBoxes: TextBox[] = [];
    const columnBoxes: TextBox[] = [];
    
    for (const box of textBoxes) {
      const isWide = box.width > wideBoxThreshold;
      const isInHeaderRegion = box.y < headerYThreshold;
      
      // Wide boxes or boxes in header region are treated as headers
      if (isWide || isInHeaderRegion) {
        wideBoxes.push(box);
      } else {
        columnBoxes.push(box);
      }
    }
    
    // Sort wide/header boxes top to bottom only
    wideBoxes.sort((a, b) => a.y - b.y);
    
    // Sort column boxes by quantized left edge (column), then by Y
    const quantumSize = 0.15; // 15% quantum - groups vertical columns while handling indents
    columnBoxes.sort((a, b) => {
      const aQuantum = Math.floor(a.x / quantumSize);
      const bQuantum = Math.floor(b.x / quantumSize);
      
      if (aQuantum !== bQuantum) {
        return aQuantum - bQuantum; // Different columns
      }
      return a.y - b.y; // Same column, top to bottom
    });
    
    // Combine: headers first (top to bottom), then columns (left to right, top to bottom)
    return [...wideBoxes, ...columnBoxes];
  }, [textBoxes]);

  if (!enabled || isLoading || textBoxes.length === 0) {
    return null;
  }

  return (
    <>
      {/* Text overlay layer */}
      <div
        ref={overlayRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'auto',
          zIndex: 6,
          userSelect: selectionEnabled ? 'text' : 'none',
          WebkitUserSelect: selectionEnabled ? 'text' : 'none',
          WebkitTouchCallout: selectionEnabled ? 'default' : 'none',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
      >
        {sortedTextBoxes.map((box, index) => {
          // Convert normalized (0-1) coordinates to pixel coordinates
          const pixelX = box.x * imageWidth;
          const pixelY = box.y * imageHeight;
          const pixelEndX = (box.x + box.width) * imageWidth;
          const pixelEndY = (box.y + box.height) * imageHeight;
          
          // Convert pixel coordinates to canvas/viewport coordinates
          const pos = imageToCanvasCoords(pixelX, pixelY);
          const endPos = imageToCanvasCoords(pixelEndX, pixelEndY);
          const width = endPos.x - pos.x;
          const height = endPos.y - pos.y;

          // Debug logging for first 3 boxes
          if (index < 3) {
            console.log(`📝 Rendering text box #${index}:`, {
              text: box.text,
              normalized: { x: box.x, y: box.y, w: box.width, h: box.height },
              pixel: { x: pixelX, y: pixelY, w: pixelEndX - pixelX, h: pixelEndY - pixelY },
              canvas: { x: pos.x, y: pos.y, w: width, h: height }
            });
          }

          // Calculate letter-spacing to stretch/compress text to fit detected width
          // Measure actual text width using canvas with the same font as rendered
          const fontSize = height * 0.8;
          const fontFamily = "'Inter', system-ui, sans-serif";
          const ctx = measureCanvasRef.current?.getContext('2d');
          let letterSpacing = 0;
          
          if (ctx && box.text.length > 1 && width > 0) {
            // Use same font as the rendered text for accurate measurement
            ctx.font = `${fontSize}px ${fontFamily}`;
            const measuredWidth = ctx.measureText(box.text).width;
            
            // Guard against whitespace-only or degenerate boxes
            if (measuredWidth > 0 && !isNaN(measuredWidth)) {
              // Calculate letter-spacing needed to stretch/compress to target width
              // Distribute the difference across the gaps between characters
              letterSpacing = (width - measuredWidth) / (box.text.length - 1);
              
              // Clamp extreme values to avoid pathological spacing
              letterSpacing = Math.max(-fontSize * 0.5, Math.min(fontSize * 2, letterSpacing));
            }
          }

          return (
            <div
              key={box.id}
              style={{
                position: 'absolute',
                left: pos.x,
                top: pos.y,
                width,
                height,
                color: 'transparent',
                overflow: 'hidden',
                fontSize: `${fontSize}px`,
                fontFamily,
                lineHeight: `${height}px`,
                letterSpacing: `${letterSpacing}px`,
                whiteSpace: 'nowrap',
                cursor: selectionEnabled ? 'text' : 'default',
                userSelect: selectionEnabled ? 'text' : 'none',
                WebkitUserSelect: selectionEnabled ? 'text' : 'none',
                msUserSelect: selectionEnabled ? 'text' : 'none',
              }}
              data-text-box-id={box.id}
            >
              {box.text}
            </div>
          );
        })}
      </div>

      {/* Comment button for selected text */}
      {selectedText && selectionPosition && (
        <button
          className="absolute rounded-full flex items-center justify-center text-white font-bold text-xs"
          style={{
            left: selectionPosition.x - 12,
            top: selectionPosition.y - 34,
            width: '24px',
            height: '24px',
            backgroundColor: indicatorColor,
            boxShadow: '0 0 0 3px white, 0 0 8px rgba(0, 0, 0, 0.3)',
            cursor: 'pointer',
            zIndex: 20,
            border: 'none',
            padding: 0,
          }}
          onClick={() => {
            onTextSelected?.(selectedText, selectionPosition);
            setSelectedText("");
            setSelectionPosition(null);
            window.getSelection()?.removeAllRanges();
          }}
          aria-label="Add comment to selected text"
          data-testid="button-comment-selected-text"
        >
          <MessageCircle className="w-3 h-3" style={{ strokeWidth: 2.5 }} />
        </button>
      )}
    </>
  );
}

interface MainViewingAreaProps {
  document: Document;
  versions: DocumentVersion[];
  currentVersion?: DocumentVersion;
  pages: DocumentPage[];
  currentPage: number;
  currentPageId: string | null;
  onVersionChange: (versionId: string) => void;
  onPageChange: (pageId: string, pageNumber: number) => void;
  isLoading: boolean;
  viewMode: "comment" | "browse";
  currentUser: { id: string; name: string; email: string } | null;
  onLowRatingIndicator?: (handler: (rating: number, position: { x: number; y: number }) => void) => void;
  initialIndicatorNumber?: number | null;
  onIndicatorChange: (indicatorNumber: number | null) => void;
  isReordering?: boolean;
  isTogglingVisibility?: boolean;
}






export function MainViewingArea({
  document,
  versions,
  currentVersion,
  pages,
  currentPage,
  currentPageId,
  onVersionChange,
  onPageChange,
  isLoading,
  viewMode,
  currentUser,
  onLowRatingIndicator,
  initialIndicatorNumber = null,
  onIndicatorChange,
  isReordering = false,
  isTogglingVisibility = false,
}: MainViewingAreaProps) {

  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();
  const isMobilePortrait = useIsMobilePortrait();
  const { toast } = useToast();
  
  // Document users query for mentions
  const { data: documentUsers = [] } = useQuery<DocumentUser[]>({
    queryKey: ['/api/documents', document.id, 'users'],
    enabled: !!document.id,
  });

  
  // Eye toggle state - controls visibility of all comments
  const [showAllComments, setShowAllComments] = useState(true);
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Text hover state - tracks when mouse is over text regions to disable click-to-comment
  const [isHoveringText, setIsHoveringText] = useState(false);
  
  // Comment thread state
  const [activeIndicator, setActiveIndicator] = useState<PageIndicator | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [newComment, setNewComment] = useState("");
  const [mentionedDocUserIds, setMentionedDocUserIds] = useState<Set<string>>(new Set());
  const shouldIgnoreRestoration = useRef(false);
  const [dragJustCompleted, setDragJustCompleted] = useState(false);
  
  // AI comment improvement state
  const [isImprovingComment, setIsImprovingComment] = useState(false);
  const [improvedCommentSuggestion, setImprovedCommentSuggestion] = useState<string | null>(null);
  
  // Dialog visibility state - separate from indicator state to allow optimistic hiding
  const [dialogHidden, setDialogHidden] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const commentsScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImages, setSelectedImages] = useState<Array<{
    id: string;
    file?: File;
    url?: string;
    storageUrl?: string;
    storageThumbnailUrl?: string;
    status: 'uploading' | 'uploaded' | 'error';
    originalFilename: string;
  }>>([]);
  const [lightboxImage, setLightboxImage] = useState<{
    url: string;
    filename: string;
  } | null>(null);
  
  // Drawing box state - now indicator-specific
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedTool, setSelectedTool] = useState<'box' | 'arrow' | 'freedraw'>('box');
  const [indicatorDrawingBoxes, setIndicatorDrawingBoxes] = useState<Map<number, any>>(new Map());
  
  // Multi-drawing support: track currently drawing shape and completed shapes
  const [currentDrawingShape, setCurrentDrawingShape] = useState<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    type?: 'box' | 'arrow' | 'freedraw';
    points?: { x: number; y: number }[];
  } | null>(null);
  const [completedDrawingShapes, setCompletedDrawingShapes] = useState<Array<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    type?: 'box' | 'arrow' | 'freedraw';
    points?: { x: number; y: number }[];
  }>>([]);
  

  
  // IRONCLAD VALIDATION: Pending indicator (not sent to server until it has a comment)
  const [pendingIndicator, setPendingIndicator] = useState<{
    x: number;
    y: number;
    canvasX: number;
    canvasY: number;
    number: number;
    selectedText?: string;
  } | null>(null);

  // Clear AI suggestion when indicator changes
  useEffect(() => {
    setImprovedCommentSuggestion(null);
    setIsImprovingComment(false);
  }, [activeIndicator?.id, pendingIndicator?.number]);

  // Version restriction modal state
  const [showVersionModal, setShowVersionModal] = useState(false);

  // Shared dialog close handler
  const closeDialog = async () => {
    // Reset dialog visibility flag
    setDialogHidden(false);
    
    // Always clear textarea content and pending mentions when closing dialog
    setNewComment("");
    setMentionedDocUserIds(new Set());
    
    if (pendingIndicator) {
      // IRONCLAD VALIDATION: Discard pending indicator (never sent to server)
      // FIXED: Also clean up any temporary drawing box for the pending indicator
      setIndicatorDrawingBoxes(prev => {
        const newMap = new Map(prev);
        newMap.delete(pendingIndicator.number);
        return newMap;
      });
      console.log(`🗑️ Cleaned up temporary drawing box for cancelled indicator #${pendingIndicator.number}`);
      setPendingIndicator(null);
    } else if (activeIndicator) {
      // Real indicator - check for comments before deciding
      try {
        const response = await fetch(`/api/indicators/${activeIndicator.id}/comments`);
        const latestComments = await response.json();
        const hasComments = latestComments && latestComments.length > 0;
        
        if (!hasComments) {
          // NO COMMENT = NO INDICATOR (strict rule)
          deleteIndicatorMutation.mutate(activeIndicator.id);
        }
      } catch (error) {
        // If we can't verify comments, err on the side of deletion
        console.warn("Could not verify comments, deleting indicator:", error);
        deleteIndicatorMutation.mutate(activeIndicator.id);
      }
    }
    
    // Prevent restoration until URL actually changes
    shouldIgnoreRestoration.current = true;
    
    // Clear indicator from URL and local state
    onIndicatorChange(null);
    setActiveIndicator(null);
    setPopoverPosition(null);
  };

  // Callback for when version restriction is triggered
  const handleVersionRestricted = useCallback(() => {
    setShowVersionModal(true);
  }, []);

  // Navigate to latest version
  const handleGoToLatest = useCallback(() => {
    setShowVersionModal(false);
    // Use the proper URL pattern: /doc/{id}/comment/v/{version}/p/1
    setLocation(`/doc/${document.id}/comment/v/${document.currentVersion}/p/1`);
  }, [document.id, document.currentVersion, setLocation]);

  // Dynamic cursor state
  const [currentCursor, setCurrentCursor] = useState<string>('help');

  // Fullscreen functionality
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // ESC key handler for exiting fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.document.addEventListener('keydown', handleKeyDown);
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Drawing functions will be defined after mutations are initialized



  


  // Look up page by ID first (stable across reorders), fallback to array index
  const currentPageData = currentPageId 
    ? pages.find(p => p.id === currentPageId) 
    : pages[currentPage - 1];

  // Initialize drawing boxes from localStorage when page changes
  useEffect(() => {
    if (!currentPageData?.id) return;
    
    try {
      const savedBoxes = localStorage.getItem(`indicatorDrawingBoxes_${currentPageData.id}`);
      if (savedBoxes) {
        const parsedBoxes = JSON.parse(savedBoxes);
        // Convert object to Map, ensuring indicator numbers are converted to numbers
        const boxMap = new Map<number, {
          startX: number;
          startY: number;
          endX: number;
          endY: number;
        }>();
        
        for (const [indicatorNumber, boxData] of Object.entries(parsedBoxes)) {
          const numericKey = parseInt(indicatorNumber, 10);
          if (!isNaN(numericKey) && typeof boxData === 'object' && boxData) {
            boxMap.set(numericKey, boxData as any);
          }
        }
        
        setIndicatorDrawingBoxes(boxMap);
        console.log(`📦 Loaded ${boxMap.size} drawing boxes from localStorage for page ${currentPageData.id}`);
      } else {
        // No saved boxes for this page - initialize empty map
        setIndicatorDrawingBoxes(new Map());
      }
    } catch (error) {
      console.warn('Failed to load drawing boxes from localStorage:', error);
      // Initialize empty map on error
      setIndicatorDrawingBoxes(new Map());
    }
  }, [currentPageData?.id]);

  // Canvas viewer integration
  const { data: pageIndicators = [], refetch: refetchIndicators } = useQuery<PageIndicator[]>({
    queryKey: ["/api/pages", currentPageData?.id, "indicators"],
    enabled: !!currentPageData?.id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Fetch text boxes for current page to check if OCR processing is needed
  const { data: pageTextBoxes = [] } = useQuery<TextBox[]>({
    queryKey: ['/api/pages', currentPageData?.id, 'text-boxes'],
    enabled: !!currentPageData?.id,
  });

  // Debug: Log pageTextBoxes for main component
  useEffect(() => {
    console.log('🔍 Main component - pageTextBoxes count:', pageTextBoxes.length, 'currentPageId:', currentPageData?.id);
  }, [pageTextBoxes.length, currentPageData?.id]);




  // Comments for active indicator (only if real indicator, not pending)
  const { data: comments = [], refetch: refetchComments, isLoading: isLoadingComments } = useQuery<CommentWithUser[]>({
    queryKey: ["/api/indicators", activeIndicator?.id, "comments"],
    enabled: !!activeIndicator?.id && !pendingIndicator,
  });

  // Auto-scroll to bottom when new comments are added (with image loading consideration)
  useEffect(() => {
    if (commentsScrollRef.current && comments.length > 0) {
      const scrollToBottom = () => {
        if (commentsScrollRef.current) {
          commentsScrollRef.current.scrollTop = commentsScrollRef.current.scrollHeight;
        }
      };

      // Initial scroll after DOM update
      setTimeout(scrollToBottom, 100);
      
      // Additional scroll after images have loaded (accounts for image height)
      setTimeout(scrollToBottom, 500);
      
      // Final scroll to ensure images are fully loaded and displayed
      setTimeout(scrollToBottom, 1000);
    }
  }, [comments.length]);



  // Filter indicators based on eye toggle state
  const filteredIndicators = showAllComments 
    ? pageIndicators 
    : [];

  // Indicator mutations (moved up before useViewerCompat)
  const { updateIndicatorMutation, deleteIndicatorMutation, updateIndicatorStatusMutation, updateIndicatorDrawingBoxMutation } = useIndicatorMutations({
    currentPageData,
    activeIndicator,
    setActiveIndicator,
    setPopoverPosition,
    documentId: document.id,
    versionNumber: currentVersion?.version || 1,
    versionId: currentVersion?.id,
    indicatorDrawingBoxes,
    setIndicatorDrawingBoxes,
  });

  // Handler for when an indicator drag completes
  const handleIndicatorDragEnd = useCallback((indicatorId: string, imageX: number, imageY: number) => {
    updateIndicatorMutation.mutate({ 
      indicatorId, 
      newPosition: { x: imageX, y: imageY } 
    });
    
    // Mark that a drag just completed to prevent immediate indicator creation
    setDragJustCompleted(true);
    setTimeout(() => setDragJustCompleted(false), 150);
  }, [updateIndicatorMutation]);

  const { canvasRef, containerRef, handlePointerDown, handlePointerMove, 
          handlePointerUp, handleWheel, drawImage, canvasToImageCoords, wasActualClick, getIndicatorAtPosition, imageToCanvasCoords,
          isDraggingIndicator, getDraggedIndicator, getDragPreviewPosition, handlePointerClick, redraw, getCurrentCursor, handleMouseMove, programmaticZoom, resetViewerState, imageStyle, state } = 
    useViewerCompat(
      currentPageData?.width || 800, 
      currentPageData?.height || 600, 
      filteredIndicators,
      pendingIndicator,
      document.defaultIndicatorColor,
      // Drawing box overlay parameters (CRITICAL FIX: Now handled by canvas viewer)
      null, // No longer using tempDrawingBox in viewer compat
      indicatorDrawingBoxes,
      isDrawing,
      activeIndicator,
      pendingIndicator, // Use pendingIndicator for pendingIndicatorForBox
      handleIndicatorDragEnd,
      isFullscreen
    );

  // Drawing overlay for boxes, arrows, and free-draw (separate from indicator management)
  // Assemble boxes array from various sources
  const drawingBoxes = useCallback(() => {
    const boxes: import("@/hooks/use-drawing-overlay").DrawingBox[] = [];
    
    // Add all completed drawing shapes (awaiting confirmation)
    if (isDrawing && completedDrawingShapes.length > 0) {
      const indicatorNumber = pendingIndicator?.number || activeIndicator?.number;
      completedDrawingShapes.forEach(shape => {
        boxes.push({
          ...shape,
          opacity: 0.3,
          strokeWidth: 3,
          label: indicatorNumber
        });
      });
    }
    
    // Add current drawing shape (actively being drawn)
    if (currentDrawingShape && isDrawing) {
      const indicatorNumber = pendingIndicator?.number || activeIndicator?.number;
      boxes.push({
        ...currentDrawingShape,
        opacity: 0.3,
        strokeWidth: 3,
        label: indicatorNumber
      });
    }
    
    // Add indicator-specific drawing box (from local state)
    if (indicatorDrawingBoxes && (activeIndicator || pendingIndicator)) {
      const targetIndicatorNumber = activeIndicator?.number || pendingIndicator?.number;
      if (targetIndicatorNumber && indicatorDrawingBoxes.has(targetIndicatorNumber)) {
        const savedData = indicatorDrawingBoxes.get(targetIndicatorNumber)!;
        
        // Handle both old format (single shape) and new format (array of shapes)
        if ('shapes' in savedData && Array.isArray(savedData.shapes)) {
          // New format: array of shapes
          savedData.shapes.forEach((shape: any) => {
            boxes.push({
              ...shape,
              opacity: 0.2,
              strokeWidth: 2,
              label: targetIndicatorNumber
            });
          });
        } else {
          // Old format: single shape
          boxes.push({
            ...savedData,
            opacity: 0.2,
            strokeWidth: 2,
            label: targetIndicatorNumber
          });
        }
      }
    }
    
    // Add persisted drawing boxes from server
    filteredIndicators.forEach(indicator => {
      if (indicator.drawingBox && typeof indicator.drawingBox === 'object') {
        const savedData = indicator.drawingBox as any;
        
        // Handle both old format (single shape) and new format (array of shapes)
        if ('shapes' in savedData && Array.isArray(savedData.shapes)) {
          // New format: array of shapes
          savedData.shapes.forEach((shape: any) => {
            boxes.push({
              ...shape,
              opacity: 0.15,
              strokeWidth: 2,
              label: indicator.number
            });
          });
        } else {
          // Old format: single shape
          boxes.push({
            ...savedData,
            opacity: 0.15,
            strokeWidth: 2,
            label: indicator.number
          });
        }
      }
    });
    
    return boxes;
  }, [currentDrawingShape, completedDrawingShapes, isDrawing, indicatorDrawingBoxes, activeIndicator, pendingIndicator, filteredIndicators, document.defaultIndicatorColor]);
  
  // Use imageToCanvasCoords from viewer compat (which is actually imageToViewportCoords from the new coordinate system)
  const { canvasRef: drawingCanvasRef, redraw: redrawDrawing } = useDrawingOverlay(
    imageToCanvasCoords,
    drawingBoxes(),
    document.defaultIndicatorColor
  );

  // Enhanced pointer handlers will be defined after drawing functions


  // CRITICAL FIX: Overlay rendering now handled by canvas viewer - no separate useEffect needed
  // Drawing boxes are now rendered synchronously as part of the canvas redraw cycle

  // Drawing handlers are now integrated into enhanced pointer handlers above

  // Reset zoom/pan when entering fullscreen mode
  useEffect(() => {
    if (isFullscreen && resetViewerState) {
      resetViewerState();
    }
  }, [isFullscreen, resetViewerState]);

  // Restore indicator from URL parameter (after canvas viewer is initialized)
  useEffect(() => {
    if (shouldIgnoreRestoration.current) return;
    
    if (initialIndicatorNumber && pageIndicators.length > 0 && imageToCanvasCoords) {
      const targetIndicator = pageIndicators.find(indicator => indicator.number === initialIndicatorNumber);
      
      if (targetIndicator) {
        // Only update if this is a different indicator (or no indicator was active)
        if (!activeIndicator || activeIndicator.number !== initialIndicatorNumber) {
          setActiveIndicator(targetIndicator);
          const indicatorCanvasPos = imageToCanvasCoords(targetIndicator.x, targetIndicator.y);
          setPopoverPosition(indicatorCanvasPos);
        }
      } else {
        // Indicator not found - show error toast and clean URL
        toast({
          title: "Comment not found",
          description: `Comment #${initialIndicatorNumber} doesn't exist on this page.`,
          variant: "destructive",
        });
        onIndicatorChange(null);
      }
    }
  }, [initialIndicatorNumber, pageIndicators, activeIndicator, imageToCanvasCoords, onIndicatorChange, toast]);

  // Reset ignore flag when URL actually changes to a different indicator or page
  useEffect(() => {
    shouldIgnoreRestoration.current = false;
  }, [initialIndicatorNumber, currentPage]);

  // Autofocus textarea when dialog opens (for both new and existing indicators)
  useEffect(() => {
    if ((activeIndicator || pendingIndicator) && !isDrawing && textareaRef.current) {
      // Multiple focus attempts with increasing delays for mobile reliability
      // iOS Safari requires user-initiated events and sufficient DOM ready time
      const timers: ReturnType<typeof setTimeout>[] = [];
      
      timers.push(setTimeout(() => {
        textareaRef.current?.focus();
      }, 150)); // Initial attempt - increased from 100ms
      
      timers.push(setTimeout(() => {
        textareaRef.current?.focus();
      }, 300)); // Second attempt for slower devices
      
      return () => timers.forEach(timer => clearTimeout(timer));
    }
  }, [activeIndicator, pendingIndicator, isDrawing]);

  // Drawing functions (now that mutations are available)
  const startDrawing = useCallback(() => {
    // Enter drawing mode - hide comment dialog and change cursor
    setIsDrawing(true);
    setCurrentDrawingShape(null);
    setCompletedDrawingShapes([]);
    
    // Change cursor to crosshair for drawing on the container
    if (containerRef.current) {
      containerRef.current.style.cursor = 'crosshair';
    }
  }, [containerRef]);

  const finishDrawing = useCallback((box: { startX: number; startY: number; endX: number; endY: number } | null) => {
    // Exit drawing mode - reset cursor and reshow comment dialog
    setIsDrawing(false);
    setCurrentDrawingShape(null);
    setCompletedDrawingShapes([]);
    
    // Reset cursor back to default on the container
    if (containerRef.current) {
      containerRef.current.style.cursor = 'default';
    }
    
    if (box && currentPageData?.id) {
      // Determine which indicator this drawing box belongs to
      const targetIndicatorNumber = pendingIndicator?.number || activeIndicator?.number;
      
      if (targetIndicatorNumber) {
        // FIXED: Only save to React state (temporary) - DO NOT save to localStorage yet
        // localStorage save will happen only on successful comment submission
        setIndicatorDrawingBoxes(prev => {
          const newMap = new Map(prev);
          newMap.set(targetIndicatorNumber, box);
          return newMap;
        });
        
        console.log(`📦 Temporarily stored drawing box for indicator #${targetIndicatorNumber} (will persist on comment submission):`, box);
        
        // Only save to database if we have an active indicator with ID
        if (activeIndicator && activeIndicator.id) {
          updateIndicatorDrawingBoxMutation.mutate({
            indicatorId: activeIndicator.id,
            drawingBox: box
          });
        }
      }
    }
  }, [activeIndicator, pendingIndicator, currentPageData, setIndicatorDrawingBoxes, updateIndicatorDrawingBoxMutation, canvasRef]);

  const cancelDrawing = useCallback(() => {
    setIsDrawing(false);
    setCurrentDrawingShape(null);
    setCompletedDrawingShapes([]);
    
    // Reset cursor back to default on the container
    if (containerRef.current) {
      containerRef.current.style.cursor = 'default';
    }
  }, [containerRef]);
  
  // Toolbar confirm handler - saves all completed shapes
  const confirmDrawing = useCallback(() => {
    if (completedDrawingShapes.length > 0) {
      // Save all shapes as an array in the drawingBox field
      const allShapes = { shapes: completedDrawingShapes };
      
      // Exit drawing mode
      setIsDrawing(false);
      setCurrentDrawingShape(null);
      setCompletedDrawingShapes([]);
      
      // Reset cursor back to default on the container
      if (containerRef.current) {
        containerRef.current.style.cursor = 'default';
      }
      
      if (currentPageData?.id) {
        // Determine which indicator this drawing box belongs to
        const targetIndicatorNumber = pendingIndicator?.number || activeIndicator?.number;
        
        if (targetIndicatorNumber) {
          // Save to React state (temporary) - DO NOT save to localStorage yet
          // localStorage save will happen only on successful comment submission
          setIndicatorDrawingBoxes(prev => {
            const newMap = new Map(prev);
            newMap.set(targetIndicatorNumber, allShapes);
            return newMap;
          });
          
          console.log(`📦 Temporarily stored ${completedDrawingShapes.length} drawing shapes for indicator #${targetIndicatorNumber}:`, allShapes);
          
          // Only save to database if we have an active indicator with ID
          if (activeIndicator && activeIndicator.id) {
            updateIndicatorDrawingBoxMutation.mutate({
              indicatorId: activeIndicator.id,
              drawingBox: allShapes
            });
          }
        }
      }
    }
  }, [completedDrawingShapes, activeIndicator, pendingIndicator, currentPageData, setIndicatorDrawingBoxes, updateIndicatorDrawingBoxMutation, containerRef]);
  
  // Toolbar cancel handler - discards all drawing shapes
  const cancelDrawingToolbar = useCallback(() => {
    cancelDrawing();
  }, [cancelDrawing]);

  const clearDrawing = useCallback(() => {
    // For active indicator: clear from database AND state/localStorage
    if (activeIndicator && activeIndicator.id) {
      updateIndicatorDrawingBoxMutation.mutate({
        indicatorId: activeIndicator.id,
        drawingBox: null
      });
      
      // Also clear from React state
      setIndicatorDrawingBoxes(prev => {
        const newMap = new Map(prev);
        newMap.delete(activeIndicator.number);
        return newMap;
      });
      
      // Also clear from localStorage
      if (currentPageData?.id) {
        const storageKey = `indicatorDrawingBoxes_${currentPageData.id}`;
        const savedBoxes = JSON.parse(localStorage.getItem(storageKey) || '{}');
        delete savedBoxes[activeIndicator.number];
        localStorage.setItem(storageKey, JSON.stringify(savedBoxes));
      }
      
      console.log(`🧹 Cleared drawing box for active indicator #${activeIndicator.number}`);
    }
    
    // For pending indicator: clear from React state and localStorage
    if (pendingIndicator) {
      setIndicatorDrawingBoxes(prev => {
        const newMap = new Map(prev);
        newMap.delete(pendingIndicator.number);
        return newMap;
      });
      
      // Also clear from localStorage if exists
      if (currentPageData?.id) {
        const storageKey = `indicatorDrawingBoxes_${currentPageData.id}`;
        const savedBoxes = JSON.parse(localStorage.getItem(storageKey) || '{}');
        delete savedBoxes[pendingIndicator.number];
        localStorage.setItem(storageKey, JSON.stringify(savedBoxes));
      }
      
      console.log(`🧹 Cleared drawing box for pending indicator #${pendingIndicator.number}`);
    }
  }, [activeIndicator, pendingIndicator, currentPageData, setIndicatorDrawingBoxes, updateIndicatorDrawingBoxMutation]);

  // Enhanced pointer handlers that integrate drawing with normal canvas interactions
  const enhancedHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Don't handle events when dialog is open (allow clicks to pass through to dialog overlay)
    if ((activeIndicator || pendingIndicator) && !isDrawing) {
      return;
    }
    
    // Don't handle click-to-comment when hovering over text (allow text selection)
    if (isHoveringText && !isDrawing) {
      return;
    }
    
    if (isDrawing) {
      // Drawing mode - capture starting position
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      
      // Convert to image coordinates immediately
      const imageCoords = canvasToImageCoords(canvasX, canvasY);
      
      // Initialize drawing based on tool type
      if (selectedTool === 'freedraw') {
        setCurrentDrawingShape({
          startX: imageCoords.x,
          startY: imageCoords.y,
          endX: imageCoords.x,
          endY: imageCoords.y,
          type: 'freedraw',
          points: [{ x: imageCoords.x, y: imageCoords.y }]
        });
      } else {
        // Box and arrow use the same start/end structure
        setCurrentDrawingShape({
          startX: imageCoords.x,
          startY: imageCoords.y,
          endX: imageCoords.x,
          endY: imageCoords.y,
          type: selectedTool
        });
      }
      
      // Set pointer capture to ensure we get all events
      e.currentTarget.setPointerCapture(e.pointerId);
      return; // Don't call normal handler
    }
    
    // Normal mode - delegate to canvas viewer
    handlePointerDown(e);
  }, [isDrawing, selectedTool, containerRef, canvasToImageCoords, handlePointerDown, activeIndicator, pendingIndicator, isHoveringText]);

  const enhancedHandlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Don't handle events when dialog is open (allow clicks to pass through to dialog overlay)
    if ((activeIndicator || pendingIndicator) && !isDrawing) {
      return;
    }
    
    if (isDrawing && currentDrawingShape) {
      // Drawing mode - update current drawing shape
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      
      // Convert to image coordinates
      const imageCoords = canvasToImageCoords(canvasX, canvasY);
      
      if (currentDrawingShape.type === 'freedraw') {
        // For free draw, append points
        setCurrentDrawingShape({
          ...currentDrawingShape,
          endX: imageCoords.x,
          endY: imageCoords.y,
          points: [...(currentDrawingShape.points || []), { x: imageCoords.x, y: imageCoords.y }]
        });
      } else {
        // For box and arrow, update end position
        setCurrentDrawingShape({
          ...currentDrawingShape,
          endX: imageCoords.x,
          endY: imageCoords.y
        });
      }
      
      // Trigger redraw to show updated shape
      redrawDrawing();
      return; // Don't call normal handler
    }
    
    // Normal mode - delegate to canvas viewer  
    handlePointerMove(e);
  }, [isDrawing, currentDrawingShape, containerRef, canvasToImageCoords, handlePointerMove, redrawDrawing, activeIndicator, pendingIndicator]);

  const enhancedHandlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Don't handle events when dialog is open (allow clicks to pass through to dialog overlay)
    if ((activeIndicator || pendingIndicator) && !isDrawing) {
      return;
    }
    
    if (isDrawing && currentDrawingShape) {
      // Drawing mode - finalize current shape and add to completed shapes array
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      
      // Convert to image coordinates
      const imageCoords = canvasToImageCoords(canvasX, canvasY);
      
      // Create finalized shape with final position
      let finalizedShape;
      if (currentDrawingShape.type === 'freedraw') {
        // For free draw, add the last point
        finalizedShape = {
          ...currentDrawingShape,
          endX: imageCoords.x,
          endY: imageCoords.y,
          points: [...(currentDrawingShape.points || []), { x: imageCoords.x, y: imageCoords.y }]
        };
      } else {
        // For box and arrow, update end position
        finalizedShape = {
          ...currentDrawingShape,
          endX: imageCoords.x,
          endY: imageCoords.y
        };
      }
      
      // Check if shape has meaningful size (skip tiny accidental clicks)
      const hasMinimumSize = currentDrawingShape.type === 'freedraw'
        ? (finalizedShape.points?.length || 0) > 2  // Free draw needs at least 3 points
        : Math.abs(finalizedShape.endX - finalizedShape.startX) > 10 || Math.abs(finalizedShape.endY - finalizedShape.startY) > 10;
      
      if (hasMinimumSize) {
        // Add to completed shapes array
        setCompletedDrawingShapes(prev => [...prev, finalizedShape]);
      }
      
      // Clear current drawing shape (ready for next drawing)
      setCurrentDrawingShape(null);
      
      // Release pointer capture
      e.currentTarget.releasePointerCapture(e.pointerId);
      
      // Redraw to show all shapes
      redrawDrawing();
      return; // Don't call normal handler
    }
    
    // Normal mode - delegate to canvas viewer
    handlePointerUp(e);
  }, [isDrawing, currentDrawingShape, containerRef, canvasToImageCoords, redrawDrawing, handlePointerUp, activeIndicator, pendingIndicator]);

  // Comment and indicator mutations
  const { addCommentMutation, handleLowRatingIndicator, deleteCommentMutation: deleteCommentMut } = useCommentIndicatorMutations({
    currentPageData,
    currentUser,
    document,
    pageIndicators,
    activeIndicator,
    pendingIndicator,
    setActiveIndicator,
    setPendingIndicator,
    setNewComment,
    deleteIndicatorMutation,
    documentId: document.id,
    versionNumber: currentVersion?.version || 1,
    versionId: currentVersion?.id,
    indicatorDrawingBoxes,
    setIndicatorDrawingBoxes,
  });

  // Main viewing area utilities (extracted)
  const {
    editingCommentId,
    setEditingCommentId,
    editingCommentText,
    setEditingCommentText,
    editCommentMutation,
    handlePreviousPage,
    handleNextPage,
    getStatusColor,
    getStatusText,
    formatRelativeTime,
  } = useMainViewingAreaUtils({
    activeIndicator,
    currentPage,
    currentPageId,
    pages,
    onPageChange,
  });

  // Immediate image upload mutation (triggered on file selection)
  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      return response.json();
    },
  });

  // AI comment improvement mutation
  const improveCommentMutation = useMutation({
    mutationFn: async ({ comment, imageUrl, position, otherComments }: {
      comment: string;
      imageUrl: string;
      position: { x: number; y: number; percentageX: number; percentageY: number };
      otherComments: string[];
    }) => {
      const response = await fetch('/api/comments/improve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          originalComment: comment,
          imageUrl: imageUrl,
          indicatorPosition: position,
          otherComments,
          pageNumber: currentPage,
          documentName: document.name,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `Failed to improve comment (${response.status})`);
      }

      const data = await response.json();
      return data.improvedComment as string;
    },
    onSuccess: (improvedComment: string) => {
      setImprovedCommentSuggestion(improvedComment);
      setIsImprovingComment(false);
    },
    onError: (error) => {
      console.error('Comment improvement error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Please try again later.';
      toast({
        title: "Failed to improve comment",
        description: errorMessage,
        variant: "destructive",
      });
      setIsImprovingComment(false);
    },
  });

  // OCR processing mutation - now per-page
  const processOcrMutation = useMutation({
    mutationFn: async (pageId: string) => {
      const response = await apiRequest("POST", `/api/pages/${pageId}/process-ocr`);
      return await response.json() as { textBoxCount: number };
    },
    onSuccess: (data: { textBoxCount: number }, pageId: string) => {
      toast({
        title: "Text extraction complete",
        description: `Found ${data.textBoxCount} text regions on this page.`,
      });
      // Refetch text boxes for this page
      queryClient.invalidateQueries({ queryKey: ['/api/pages', pageId, 'text-boxes'] });
    },
    onError: () => {
      toast({
        title: "Failed to extract text",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });

  // Manual drag state to avoid dropzone blocking textarea touch events
  const [isDragActive, setIsDragActive] = useState(false);

  const removeImage = (id: string) => {
    setSelectedImages(prev => prev.filter(img => img.id !== id));
  };

  // AI comment improvement handlers
  const handleImproveComment = async () => {
    if (!newComment.trim() || !currentPageData) return;
    
    setIsImprovingComment(true);
    setImprovedCommentSuggestion(null);

    // Get the indicator position
    const indicator = activeIndicator || pendingIndicator;
    if (!indicator) {
      setIsImprovingComment(false);
      return;
    }

    // Calculate percentage position based on image dimensions
    const imageWidth = currentPageData.width || 1;
    const imageHeight = currentPageData.height || 1;
    const percentageX = Math.round((indicator.x / imageWidth) * 100);
    const percentageY = Math.round((indicator.y / imageHeight) * 100);

    // Get other comments on this page for context
    const otherComments = comments
      .filter(c => c.id !== editingCommentId) // Exclude currently editing comment
      .map(c => c.content);

    // Get the page image URL (prefer webp)
    const imageUrl = currentPageData.webpUrl || currentPageData.imageUrl;
    
    if (!imageUrl) {
      console.error('No image URL available for page');
      setIsImprovingComment(false);
      toast({
        title: "Cannot improve comment",
        description: "Page image not available.",
        variant: "destructive",
      });
      return;
    }

    try {
      await improveCommentMutation.mutateAsync({
        comment: newComment.trim(),
        imageUrl,
        position: {
          x: indicator.x,
          y: indicator.y,
          percentageX,
          percentageY,
        },
        otherComments,
      });
    } catch (error) {
      // Error handling is in the mutation
      console.error('Failed to improve comment:', error);
    }
  };

  const handleAcceptImprovement = () => {
    if (improvedCommentSuggestion) {
      setNewComment(improvedCommentSuggestion);
      setImprovedCommentSuggestion(null);
      // Focus back on textarea
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  const handleRejectImprovement = () => {
    setImprovedCommentSuggestion(null);
    // Focus back on textarea
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  // Enhanced comment submission with images and drawing box
  const submitComment = async (content: string) => {
    if (!content.trim() && selectedImages.length === 0) return;
    
    // Check if all images are uploaded
    const hasUploadingImages = selectedImages.some(img => img.status === 'uploading');
    if (hasUploadingImages) return; // Don't submit if uploads pending
    
    // Get uploaded image URLs for attachment creation
    const uploadedImages = selectedImages.filter(img => img.status === 'uploaded');
    
    // Check for indicator-specific drawing box in state
    let drawingBoxData = null;
    const targetIndicatorNumber = pendingIndicator?.number || activeIndicator?.number;
    if (targetIndicatorNumber && indicatorDrawingBoxes.has(targetIndicatorNumber)) {
      drawingBoxData = indicatorDrawingBoxes.get(targetIndicatorNumber);
      console.log(`📦 Including drawing box for indicator #${targetIndicatorNumber}:`, drawingBoxData);
    }
    
    // OPTIMISTIC: Hide dialog immediately for instant UX
    setDialogHidden(true);
    
    // Clear images immediately (optimistic)
    setSelectedImages([]);
    
    // Clear comment text and mentions immediately (optimistic)
    setNewComment("");
    const idsToSend = Array.from(mentionedDocUserIds);
    setMentionedDocUserIds(new Set());
    
    // Fire mutation in background without blocking
    addCommentMutation.mutateAsync({
      content: content.trim() || "",
      drawingBox: drawingBoxData,
      mentionedUserIds: idsToSend
    }).then((result) => {
      const commentId = result?.comment?.id;
      
      // Clear the indicator-specific drawing box from React state after successful submission
      // NOTE: Box is now persisted in localStorage by the mutation's onSuccess handler
      if (targetIndicatorNumber && drawingBoxData) {
        setIndicatorDrawingBoxes(prev => {
          const newMap = new Map(prev);
          newMap.delete(targetIndicatorNumber);
          return newMap;
        });
      }
      
      // Create attachment records for uploaded images in background
      if (uploadedImages.length > 0 && commentId) {
        Promise.all(
          uploadedImages.map(img => 
            fetch(`/api/comments/${commentId}/attachments`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                filename: img.originalFilename.replace(/\.[^/.]+$/, "") + ".webp",
                originalFilename: img.originalFilename,
                mimeType: "image/webp",
                size: 0, // Size will be updated by backend
                storageUrl: img.storageUrl, // Use raw storage URL for database
                thumbnailUrl: img.storageThumbnailUrl // Use raw thumbnail URL for database
              })
            })
          )
        ).then(() => {
          // Force refresh comments after attachments created
          queryClient.invalidateQueries({ queryKey: ['/api/indicators', result?.comment?.indicatorId || activeIndicator?.id, 'comments'] });
        }).catch((error) => {
          console.error('Failed to create attachments:', error);
          toast({
            title: "Attachment upload failed",
            description: "Your comment was saved but images could not be attached.",
            variant: "destructive"
          });
        });
      }
      
      // After successful submission, properly close the dialog
      closeDialog();
    }).catch((error) => {
      console.error('Failed to submit comment:', error);
      
      // On error: restore state and re-show dialog
      setNewComment(content);
      setSelectedImages(uploadedImages);
      setDialogHidden(false);
      
      toast({
        title: "Failed to submit comment",
        description: "Please try again.",
        variant: "destructive"
      });
    });
  };

  // Arrow key navigation for comment mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys in comment mode
      if (viewMode !== 'comment') return;
      
      // Prevent navigation if user is typing in an input field or textarea
      const activeElement = window.document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' || 
        activeElement.getAttribute('contenteditable') === 'true'
      )) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePreviousPage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNextPage();
      }
    };

    window.document.addEventListener('keydown', handleKeyDown);
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, handlePreviousPage, handleNextPage]);

  // All effects and event handling logic extracted to custom hook
  useMainViewingAreaEffects({
    onLowRatingIndicator,
    handleLowRatingIndicator,
    pageIndicators,
    redraw,
    activeIndicator,
    pendingIndicator,
    textareaRef,
    getCurrentCursor,
    setCurrentCursor,
    handlePointerClick,
    getIndicatorAtPosition,
    imageToCanvasCoords,
    canvasToImageCoords,
    setActiveIndicator,
    setPopoverPosition,
    viewMode,
    dragJustCompleted,
    setDragJustCompleted,
    isDraggingIndicator,
    setPendingIndicator,
    onIndicatorChange,
    currentVersion: currentVersion?.version,
    latestVersion: document.currentVersion,
    onVersionRestricted: handleVersionRestricted,
    currentPageData,
    drawImage,
  });

  // Processing status detection - poll whenever the current version isn't completed,
  // regardless of whether pages have loaded. This keeps the header badge live.
  const processingVersionId =
    currentVersion && currentVersion.processingStatus !== 'completed'
      ? currentVersion.id
      : null;

  const processingStatus = useProcessingStatus(
    processingVersionId,
    () => {
      // When processing finishes, refresh the document so the header badge clears
      queryClient.invalidateQueries({ queryKey: ['/api/documents', document.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents', document.id, 'with-versions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents'] });
    }
  );

  // Reusable Navigation Component
  const NavigationControls = ({ className = "", isFixed = false }: { className?: string; isFixed?: boolean }) => {
    const visiblePages = pages.filter(p => !p.hidden);
    const visiblePageCount = visiblePages.length;
    
    // Guard against no visible pages
    if (visiblePageCount === 0) {
      return (
        <div className={`flex items-center justify-center px-2 py-2 bg-muted/50 dark:bg-muted/20 ${className}`}>
          <span className="text-xs text-muted-foreground">No visible pages</span>
        </div>
      );
    }
    
    return (
      <div className={`flex items-center justify-between px-2 py-2 bg-muted/50 dark:bg-muted/20 ${className}`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePreviousPage}
          disabled={currentPage <= 1}
          className="h-8 px-1.5 sm:px-2 text-xs"
        >
          <ChevronLeft className="w-3 h-3 sm:mr-1" />
          <span className="hidden sm:inline">Prev</span>
        </Button>
        
        <div className="flex items-center gap-1 sm:gap-2">
          <span className="text-xs text-muted-foreground font-medium">
            {currentPage} / {visiblePageCount}
          </span>
          {!isMobile && (
            <span className="text-xs text-muted-foreground">
              pages
            </span>
          )}
          

        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNextPage}
          disabled={currentPage >= visiblePageCount}
          className="h-8 px-1.5 sm:px-2 text-xs"
          title={`Page ${currentPage} of ${visiblePageCount}`}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-3 h-3 sm:ml-1" />
        </Button>
      </div>
    );
  };



  return (
    <div className="bg-background border border-border rounded-lg overflow-hidden">
      {/* Compact Header */}
      <div className="px-3 py-2 border-b border-border bg-muted/50 dark:bg-muted/20">
        <div className="flex items-center justify-between gap-2 relative">
          {/* Document Info - More Compact */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>v{currentVersion?.version || 1}</span>
              {currentVersion && (
                <>
                  <span>•</span>
                  {currentVersion.processingStatus === "completed" && !processingStatus.isProcessing ? (
                    <span className="text-muted-foreground">
                      {formatRelativeTime(currentVersion.createdAt)}
                    </span>
                  ) : (
                    <span className={getStatusColor(
                      processingStatus.isProcessing ? processingStatus.status : currentVersion.processingStatus
                    )}>
                      {getStatusText(
                        processingStatus.isProcessing ? processingStatus.status : currentVersion.processingStatus,
                        processingStatus.isProcessing ? processingStatus.progress : currentVersion.processingProgress
                      )}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Saving Indicator - Centered */}
          {(isReordering || isTogglingVisibility) && (
            <div className="absolute left-1/2 -translate-x-1/2 bg-background/95 backdrop-blur-sm px-3 py-1 rounded-full shadow-md border border-border flex items-center gap-2 z-10">
              <Loader2 className="h-3 w-3 animate-spin" style={{ color: document.defaultIndicatorColor }} />
              <span className="text-xs text-muted-foreground">Saving...</span>
            </div>
          )}


          {/* Compact Controls */}
          <div className="flex items-center gap-1">
            {/* Zoom Controls */}
            <Button
              variant="ghost"
              size="sm"
              className="w-6 h-6 p-0 opacity-50 hover:opacity-100 active:opacity-100 transition-opacity disabled:opacity-20 disabled:cursor-not-allowed"
              onClick={() => programmaticZoom(-1)}
              disabled={state.scale <= state.minScale}
              title="Zoom out"
              data-testid="button-zoom-out"
            >
              <ZoomOut className="w-3 h-3 text-foreground" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              className="w-6 h-6 p-0 opacity-50 hover:opacity-100 active:opacity-100 transition-opacity disabled:opacity-20 disabled:cursor-not-allowed"
              onClick={() => programmaticZoom(1)}
              disabled={state.scale >= state.maxScale}
              title="Zoom in"
              data-testid="button-zoom-in"
            >
              <ZoomIn className="w-3 h-3 text-foreground" />
            </Button>
            
            {/* Vertical Separator */}
            <Separator orientation="vertical" className="h-4 mx-1" />
            
            {/* Eye Toggle Button */}
            <Button
              variant="ghost"
              size="sm"
              className="w-6 h-6 p-0 opacity-50 hover:opacity-100 active:opacity-100 transition-opacity"
              onClick={() => setShowAllComments(!showAllComments)}
              title={showAllComments ? "Hide all comments" : "Show all comments"}
              data-testid="button-toggle-comments"
            >
              {showAllComments ? (
                <Eye className="w-3 h-3 text-foreground" />
              ) : (
                <EyeOff className="w-3 h-3 text-foreground" />
              )}
            </Button>
            
            {/* Fullscreen Toggle Button */}
            <Button
              variant="ghost"
              size="sm"
              className="w-6 h-6 p-0 opacity-50 hover:opacity-100 active:opacity-100 transition-opacity"
              onClick={() => toggleFullscreen()}
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              data-testid="button-fullscreen"
            >
              {isFullscreen ? (
                <Minimize className="w-3 h-3 text-foreground" />
              ) : (
                <Maximize className="w-3 h-3 text-foreground" />
              )}
            </Button>
            
            {/* Process Page Button - Only show in comment mode if page has no text boxes */}
            {viewMode === "comment" && currentPageId && pageTextBoxes.length === 0 && (
              <>
                <Separator orientation="vertical" className="h-4 mx-1" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 py-0 opacity-50 hover:opacity-100 active:opacity-100 transition-opacity text-xs"
                  onClick={() => currentPageId && processOcrMutation.mutate(currentPageId)}
                  disabled={processOcrMutation.isPending}
                  title="Extract text from this page to enable text selection"
                  data-testid="button-process-page"
                >
                  {processOcrMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <FileText className="w-3 h-3 text-foreground" />
                  )}
                </Button>
              </>
            )}

          </div>
        </div>
      </div>

      {/* Navigation Controls - Always visible above canvas */}
      {pages.length > 0 && (
        <div className="border-b border-border">
          <NavigationControls />
        </div>
      )}

      {/* Main Viewing Area - No Extra Padding */}
      <div className="bg-muted/30 dark:bg-muted/10">
        <div 
          ref={containerRef}
          className={`bg-background border-y border-border overflow-hidden touch-none relative ${
            isFullscreen 
              ? "fixed inset-0 border-0" 
              : ""
          } ${
            (activeIndicator || pendingIndicator) && !isDrawing
              ? "pointer-events-none"
              : ""
          }`}
          style={{
            ...(isFullscreen 
              ? { 
                  width: "100vw", 
                  height: "100vh",
                  left: 0,
                  top: 0,
                  position: "fixed",
                  zIndex: 50,
                }
              : { 
                  aspectRatio: currentPageData 
                    ? `${currentPageData.width}/${currentPageData.height}` 
                    : "16/9",
                  width: "100%"
                }),
            touchAction: 'none',
            cursor: currentCursor,
            userSelect: pageTextBoxes.length > 0 ? 'auto' : 'none',
            WebkitUserSelect: pageTextBoxes.length > 0 ? 'auto' : 'none',
            overscrollBehavior: 'none',
          } as React.CSSProperties}
          onPointerDown={enhancedHandlePointerDown}
          onPointerMove={enhancedHandlePointerMove}
          onPointerUp={enhancedHandlePointerUp}
          onWheel={handleWheel}
          onMouseMove={!isDrawing ? handleMouseMove : undefined}
          onTouchStart={(e) => {
            // Prevent browser zoom on multi-touch
            if (e.touches.length > 1) {
              e.preventDefault();
            }
          }}
          onTouchEnd={(e) => {
            // Don't prevent default if touch is on editable content or inside dialog
            const target = e.target as HTMLElement;
            const isEditableElement = target.tagName === 'TEXTAREA' || 
                                     target.tagName === 'INPUT' || 
                                     target.isContentEditable ||
                                     target.closest('[role="dialog"]') ||
                                     target.closest('[data-input-section]') ||
                                     target.closest('[data-comment-textarea]');
            
            if (!isEditableElement) {
              // Prevent double-tap zoom only on non-editable areas
              e.preventDefault();
            }
          }}
          onTouchMove={(e) => {
            // Prevent browser scrolling/zooming during touch move
            e.preventDefault();
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : processingStatus.isProcessing ? (
            <ProcessingSkeleton 
              type="canvas"
              progress={processingStatus.progress}
              statusText={processingStatus.statusText}
              documentColor={document.defaultIndicatorColor}
              aspectRatio="16/9"
            />
          ) : currentPageData ? (
            <>
              {/* Render placeholder or image */}
              {currentPageData.isPlaceholder ? (
                <div
                  ref={canvasRef as any}
                  className="w-full h-full flex flex-col items-center justify-center gap-2 sm:gap-4 px-4"
                  style={{
                    ...imageStyle,
                    backgroundColor: document.defaultIndicatorColor + '15',
                    border: `4px dashed ${document.defaultIndicatorColor}40`
                  }}
                >
                  <div 
                    className="text-2xl sm:text-4xl font-bold tracking-wide text-center"
                    style={{ color: document.defaultIndicatorColor }}
                  >
                    {currentPageData.label || "NEW SLIDE PLACEHOLDER"}
                  </div>
                  <div 
                    className="text-sm sm:text-lg opacity-60 text-center max-w-md"
                    style={{ color: document.defaultIndicatorColor }}
                  >
                    This slide is a placeholder. Comments can still be added here.
                  </div>
                </div>
              ) : (
                <img
                  ref={canvasRef}
                  src={currentPageData.webpUrl || currentPageData.imageUrl || ''}
                  alt={`Page ${currentPage}`}
                  style={imageStyle}
                  draggable={false}
                />
              )}
              
              {/* Drawing overlay canvas for boxes, arrows, and free-draw */}
              <canvas
                ref={drawingCanvasRef}
                width={containerRef.current?.clientWidth || 800}
                height={containerRef.current?.clientHeight || 600}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              />
              
              {/* Text selection overlay - only enabled in comment mode with text boxes */}
              <TextSelectionOverlay
                pageId={currentPageData.id}
                canvasRef={canvasRef}
                containerRef={containerRef}
                imageToCanvasCoords={imageToCanvasCoords}
                onTextHover={setIsHoveringText}
                onTextSelected={(text, position) => {
                  // Convert canvas position to image coordinates for indicator
                  const imagePos = canvasToImageCoords(position.x, position.y);
                  
                  // Calculate next indicator number (same logic as low rating handler)
                  const existingNumbers = (pageIndicators || [])
                    .map(i => i.number)
                    .filter(num => typeof num === 'number' && !isNaN(num));
                  const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
                  
                  // Create indicator with selected text
                  setPendingIndicator({
                    number: nextNumber,
                    x: imagePos.x,
                    y: imagePos.y,
                    canvasX: position.x,
                    canvasY: position.y,
                    selectedText: text,
                  });
                }}
                enabled={viewMode === "comment" && pageTextBoxes.length > 0}
                selectionEnabled={!isMobilePortrait}
                indicatorColor={document.defaultIndicatorColor}
              />
              
              {/* Indicator overlays */}
              {showAllComments && filteredIndicators.map((indicator) => {
                // Skip drawing the indicator being dragged at its original position
                if (isDraggingIndicator() && getDraggedIndicator()?.id === indicator.id) {
                  return null;
                }
                
                const viewportPos = imageToCanvasCoords(indicator.x, indicator.y);
                const isHovered = getCurrentCursor && getCurrentCursor() === 'pointer';
                const isActive = activeIndicator?.id === indicator.id;
                
                return (
                  <div
                    key={indicator.id}
                    className="absolute w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs transition-opacity cursor-pointer"
                    style={{
                      left: viewportPos.x - 12,
                      top: viewportPos.y - 12,
                      backgroundColor: document.defaultIndicatorColor,
                      opacity: (indicator.status === 'skipped' || indicator.status === 'resolved') ? (isHovered ? 0.7 : 0.3) : 1,
                      zIndex: isActive ? 20 : 10,
                      boxShadow: '0 0 0 3px white, 0 0 8px rgba(0, 0, 0, 0.3)',
                    }}
                    data-testid={`indicator-${indicator.number}`}
                  >
                    {indicator.number}
                  </div>
                );
              })}
              
              {/* Pending indicator overlay */}
              {pendingIndicator && (
                <div
                  className="absolute w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs pointer-events-none"
                  style={{
                    left: pendingIndicator.canvasX - 12,
                    top: pendingIndicator.canvasY - 12,
                    backgroundColor: document.defaultIndicatorColor,
                    zIndex: 20,
                    boxShadow: '0 0 0 3px white, 0 0 8px rgba(0, 0, 0, 0.3)',
                  }}
                  data-testid="indicator-pending"
                >
                  {pendingIndicator.number}
                </div>
              )}
              
              {/* Dragged indicator preview */}
              {isDraggingIndicator() && getDraggedIndicator() && getDragPreviewPosition() && (
                <div
                  className="absolute w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs pointer-events-none"
                  style={{
                    left: getDragPreviewPosition()!.x - 12,
                    top: getDragPreviewPosition()!.y - 12,
                    backgroundColor: document.defaultIndicatorColor,
                    opacity: 0.9,
                    zIndex: 30,
                    boxShadow: `0 0 0 3px white, 0 0 20px ${document.defaultIndicatorColor}`,
                  }}
                >
                  {getDraggedIndicator()!.number}
                </div>
              )}
              
              {/* Drawing Toolbar */}
              {isDrawing && (
                <DrawingToolbar
                  selectedTool={selectedTool}
                  onToolChange={setSelectedTool}
                  onConfirm={confirmDrawing}
                  onCancel={cancelDrawingToolbar}
                  hasDrawing={completedDrawingShapes.length > 0 || !!currentDrawingShape}
                  accentColor={document.defaultIndicatorColor}
                  isFullscreen={isFullscreen}
                />
              )}

              {/* Fullscreen Toolbar */}
              {isFullscreen && (
                <FullscreenToolbar
                  onPrevious={handlePreviousPage}
                  onNext={handleNextPage}
                  onExit={() => setIsFullscreen(false)}
                  onZoomIn={() => programmaticZoom(1)}
                  onZoomOut={() => programmaticZoom(-1)}
                  canGoPrevious={currentPage > 1}
                  canGoNext={currentPage < pages.filter(p => !p.hidden).length}
                  canZoomIn={state.scale < state.maxScale}
                  canZoomOut={state.scale > state.minScale}
                  accentColor={document.defaultIndicatorColor}
                />
              )}
              
              {/* Comment Thread Dialog */}
              <CommentThreadDialog
                open={!!(activeIndicator || pendingIndicator) && !isDrawing && !dialogHidden}
                onOpenChange={(open) => {
                  if (!open) {
                    closeDialog();
                  }
                }}
                activeIndicator={activeIndicator}
                pendingIndicator={pendingIndicator}
                indicatorColor={document.defaultIndicatorColor}
                comments={comments}
                isLoadingComments={isLoadingComments}
                currentUser={currentUser}
                documentUsers={documentUsers}
                newComment={newComment}
                setNewComment={setNewComment}
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                isDragActive={isDragActive}
                setIsDragActive={setIsDragActive}
                textareaRef={textareaRef}
                commentsScrollRef={commentsScrollRef}
                fileInputRef={fileInputRef}
                editingCommentId={editingCommentId}
                setEditingCommentId={setEditingCommentId}
                editingCommentText={editingCommentText}
                setEditingCommentText={setEditingCommentText}
                indicatorDrawingBoxes={indicatorDrawingBoxes}
                onStatusChange={(indicatorId, status) => {
                  updateIndicatorStatusMutation.mutate({
                    indicatorId,
                    status
                  });
                }}
                onClose={closeDialog}
                onSubmitComment={submitComment}
                onMentionSelect={(user) => setMentionedDocUserIds(prev => new Set(prev).add(user.id))}
                onDeleteComment={(commentId) => deleteCommentMut.mutate(commentId)}
                onEditComment={(commentId, content) => {
                  editCommentMutation.mutate({ commentId, content });
                }}
                onImageUpload={(file) => uploadImageMutation.mutateAsync(file)}
                onRemoveImage={removeImage}
                onStartDrawing={startDrawing}
                onClearDrawing={clearDrawing}
                onLightboxOpen={(url, filename) => setLightboxImage({ url, filename })}
                deleteCommentPending={deleteCommentMut.isPending}
                onImproveComment={handleImproveComment}
                isImprovingComment={isImprovingComment}
                improvedCommentSuggestion={improvedCommentSuggestion}
                onAcceptImprovement={handleAcceptImprovement}
                onRejectImprovement={handleRejectImprovement}
              />

              {/* Enhanced Image Lightbox */}
              {lightboxImage && (
                <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
                  <DialogContent className="max-w-[90vw] max-h-[90vh] w-auto h-auto p-0 bg-white dark:bg-slate-800 border-border">
                    <VisuallyHidden>
                      <DialogTitle>Image Viewer</DialogTitle>
                    </VisuallyHidden>
                    <div className="relative flex flex-col max-h-[90vh]">
                      {/* Header with filename and download */}
                      <div className="flex items-center p-4 border-b border-border flex-shrink-0 bg-muted/30 dark:bg-muted/10">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            // Direct download approach for Google Cloud Storage signed URLs
                            const link = window.document.createElement('a');
                            link.style.display = 'none';
                            link.href = lightboxImage.url;
                            link.download = lightboxImage.filename;
                            link.setAttribute('download', lightboxImage.filename);
                            link.rel = 'noopener noreferrer';
                            
                            // Force download by manipulating the URL
                            const url = new URL(lightboxImage.url);
                            url.searchParams.set('response-content-disposition', `attachment; filename="${lightboxImage.filename}"`);
                            url.searchParams.set('response-content-type', 'application/octet-stream');
                            link.href = url.toString();
                            
                            window.document.body.appendChild(link);
                            link.click();
                            window.document.body.removeChild(link);
                          }}
                          className="text-muted-foreground hover:text-foreground hover:bg-muted mr-3 h-8 w-8"
                          title="Download image"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <h3 className="text-foreground font-medium truncate">{lightboxImage.filename}</h3>
                      </div>
                      
                      {/* Image container */}
                      <div className="flex items-center justify-center p-4 min-h-0 flex-1 bg-muted/10 dark:bg-muted/5">
                        <img
                          src={lightboxImage.url}
                          alt={lightboxImage.filename}
                          className="max-w-full max-h-full object-contain rounded shadow-lg"
                        />
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}
        </div>

        {/* Version Restriction Modal */}
        <VersionRestrictedModal
          isOpen={showVersionModal}
          onClose={() => setShowVersionModal(false)}
          onGoToLatest={handleGoToLatest}
          currentVersion={currentVersion?.version}
          latestVersion={document.currentVersion}
        />

      </div>
    </div>
  );
}
